/*
 * Roon Body — live reviews engine
 * Single source of truth for star ratings, review counts and review cards
 * across the storefront. Pulls live from Klaviyo's public onsite reviews API
 * (the same endpoint the Klaviyo widget itself uses) so nothing is hardcoded.
 *
 * Declarative usage (no per-page JS needed):
 *   <span data-roon-rating="store">            store-wide aggregate
 *   <span data-roon-rating="product:9066036822238">  one product's aggregate
 *     ...inside, any of:
 *       <span data-roon-stat="stars"></span>   filled with ★ icons (rating-accurate)
 *       <span data-roon-stat="rating"></span>  filled with e.g. "5.0"
 *       <span data-roon-stat="count"></span>   filled with e.g. "28"
 *       <span data-roon-stat="count-plus"></span> filled with e.g. "28+"
 *
 *   <div data-roon-reviews="store" data-roon-limit="12"></div>  default card grid
 *
 * Programmatic usage:
 *   RoonReviews.ready(function(data){ ... });   data = full API payload
 *   RoonReviews.aggregate(data, 'store'|'product:<shopifyId>') -> {rating,count,histogram}
 *   RoonReviews.normalize(data) -> [{author,title,body,quote,rating,verified,date,tags,productName,productId,image}]
 */
(function () {
  'use strict';

  var COMPANY = 'Uac89e';
  var ENDPOINT = 'https://fast.a.klaviyo.com/reviews/api/client_reviews/all/';
  var CACHE_KEY = 'roon_reviews_cache_v1';
  var TTL_MS = 10 * 60 * 1000; // 10 minutes
  var STAR_FILL = '#CAA46B';   // Roon brand tan
  var STAR_EMPTY = '#E0DACE';
  // Klaviyo custom question id for "What stands out about Roon?" (drives filter tags)
  var Q_STANDS_OUT = 'c42fe552-e729-4e18-83d2-f5d35f730ea1';
  var Q_SCENT = '0005fa61-129b-4f46-896f-55b7ce744955';

  var _promise = null;

  function tz() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'; }
    catch (e) { return 'America/Los_Angeles'; }
  }

  function fetchAll() {
    if (_promise) return _promise;
    // session cache
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var c = JSON.parse(raw);
        if (c && c.t && (Date.now() - c.t) < TTL_MS && c.d) {
          _promise = Promise.resolve(c.d);
          return _promise;
        }
      }
    } catch (e) {}

    var url = ENDPOINT + '?product_id=all&company_id=' + COMPANY +
      '&limit=200&offset=0&sort=3&filter=&type=reviews&media=false&kl_review_uuid=&subtype=all&tz=' +
      encodeURIComponent(tz());

    _promise = fetch(url, { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('reviews ' + r.status); return r.json(); })
      .then(function (d) {
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: d })); } catch (e) {}
        return d;
      });
    return _promise;
  }

  // --- aggregates -----------------------------------------------------------
  function aggregate(data, scope) {
    if (!data) return null;
    if (!scope || scope === 'store' || scope === 'all') {
      var s = data.summary || {};
      return { rating: s.star_rating, count: s.review_count, histogram: s.rating_histogram || [] };
    }
    var pid = String(scope).replace(/^product:/, '');
    var revs = data.reviews || [];
    for (var i = 0; i < revs.length; i++) {
      var p = revs[i].product;
      if (p && (String(p.shopify_id) === pid || String(p.id) === pid || String(p.external_id) === pid)) {
        return { rating: p.star_rating, count: p.review_count, histogram: p.rating_histogram || [] };
      }
    }
    return null;
  }

  // Scent display names, matched against the Klaviyo "Which scent are you
  // reviewing?" answer on Discovery Set reviews.
  var SCENT_NAMES = {
    'coastal-citron': 'coastal citron',
    'vanilla-drift': 'vanilla drift',
    'coconut-shore': 'coconut shore',
    'bare': 'bare'
  };

  function reviewMatchesScent(rev, key) {
    var name = SCENT_NAMES[key];
    if (!name) return false;
    var ans = (rev.scent || []).join(' ').toLowerCase();
    return ans.indexOf(name) > -1;
  }

  // Scent-specific aggregate for a product page: that scent's OWN reviews plus
  // Discovery Set reviews whose "Which scent…" answer is this scent. No
  // store-wide fallback — a scent PDP must never show the site-wide total.
  // Returns { rating, count, own, derived }.
  function scentAggregate(data, key, ownId) {
    var all = normalize(data);
    var own = [], derived = [];
    for (var i = 0; i < all.length; i++) {
      var r = all[i];
      if (ownId && String(r.productId) === String(ownId)) own.push(r);
      else if (reviewMatchesScent(r, key)) derived.push(r);
    }
    var list = own.concat(derived);
    if (!list.length) return { rating: null, count: 0, own: [], derived: [] };
    var sum = 0;
    for (var j = 0; j < list.length; j++) sum += (list[j].rating || 5);
    return { rating: sum / list.length, count: list.length, own: own, derived: derived };
  }

  // --- normalize reviews into a flat, render-friendly shape -----------------
  function answerArr(rev, qid) {
    var qa = rev.question_answers || {};
    if (qa[qid] && qa[qid].answer) return [].concat(qa[qid].answer);
    return [];
  }

  // Every non-hidden custom-question the reviewer answered, in a render-friendly
  // shape: {label, answer[], type, values}. Powers the expandable full-detail card.
  function allAnswersArr(rev) {
    var qa = rev.question_answers || {};
    var out = [];
    for (var k in qa) {
      if (!qa.hasOwnProperty(k)) continue;
      var v = qa[k];
      if (!v || v.hidden) continue;
      var ans = [].concat(v.answer || []).filter(function (a) { return a != null && a !== ''; });
      if (!ans.length || !v.label) continue;
      out.push({ label: v.label, answer: ans, type: v.type || '', values: v.values || null });
    }
    return out;
  }

  function normalize(data) {
    var out = [];
    var revs = (data && data.reviews) || [];
    for (var i = 0; i < revs.length; i++) {
      var r = revs[i];
      if (r.status && r.status !== 'Published') continue;
      var p = r.product || {};
      var stands = answerArr(r, Q_STANDS_OUT);
      var scent = answerArr(r, Q_SCENT);
      out.push({
        id: r.id,
        author: r.author || 'Verified Customer',
        title: (r.title || '').trim(),
        body: (r.content || '').trim(),
        quote: (r.smart_quote || r.title || r.content || '').trim(),
        rating: r.rating || 5,
        verified: !!r.verified,
        date: r.created_at,
        tags: stands.concat(scent).filter(Boolean),
        standsOut: stands,
        scent: scent,
        allAnswers: allAnswersArr(r),
        productName: p.name || '',
        productId: p.shopify_id,
        image: r.image_uuid ? imageUrl(r.image_uuid) : ''
      });
    }
    return out;
  }

  function imageUrl(uuid) {
    return 'https://reviews-media.services.klaviyo.com/abc/width:600/height:600/plain/https://klaviyo.s3.amazonaws.com/reviews/images/' + uuid;
  }

  // --- star markup ----------------------------------------------------------
  function starsHTML(rating, opts) {
    opts = opts || {};
    var fill = opts.fill || STAR_FILL;
    var empty = opts.empty || STAR_EMPTY;
    var n = Math.round(rating || 5);
    var glyphs = '';
    for (var i = 1; i <= 5; i++) {
      glyphs += '<span style="color:' + (i <= n ? fill : empty) + '">★</span>';
    }
    return '<span class="roon-stars" aria-label="' + (rating || 5) + ' out of 5 stars" style="letter-spacing:.06em;white-space:nowrap">' + glyphs + '</span>';
  }

  // --- declarative fills ----------------------------------------------------
  function fmtRating(v) {
    if (v == null) return '';
    return (Math.round(v * 10) / 10).toFixed(1);
  }

  function fillRatings(data) {
    var nodes = document.querySelectorAll('[data-roon-rating]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.getAttribute('data-roon-filled')) continue; // already done (idempotent for re-runs)
      var scope = el.getAttribute('data-roon-rating');
      var scentKey = el.getAttribute('data-roon-scent');
      var agg;
      if (scentKey) {
        // Scent product page: count this scent's own reviews + Discovery Set
        // reviews that picked this scent. NO store fallback (scent-specific).
        var ownId = (scope || '').replace(/^product:/, '');
        agg = scentAggregate(data, scentKey, ownId);
        if (!agg.count) {
          el.classList.add('roon-pdp-rating--empty');
          el.innerHTML = '<span class="roon-pdp-rating__empty">No reviews yet — be the first</span>';
          el.setAttribute('data-roon-filled', '1');
          continue;
        }
      } else {
        agg = aggregate(data, scope);
        // Sparse single-SKU pages fall back to the store-wide aggregate so every
        // product surfaces the full body of (mostly Discovery Set) reviews.
        if ((!agg || !agg.count) && scope && scope.indexOf('product:') === 0) {
          agg = aggregate(data, 'store');
        }
      }
      if (!agg || agg.count == null) continue;
      var stats = el.querySelectorAll('[data-roon-stat]');
      // if the element itself is the stat carrier
      var list = stats.length ? stats : [el];
      for (var j = 0; j < list.length; j++) {
        var sEl = list[j];
        var kind = sEl.getAttribute('data-roon-stat') || 'count';
        if (kind === 'stars') sEl.innerHTML = starsHTML(agg.rating, readStarOpts(sEl));
        else if (kind === 'rating') sEl.textContent = fmtRating(agg.rating);
        else if (kind === 'count') sEl.textContent = String(agg.count);
        else if (kind === 'count-plus') sEl.textContent = agg.count + '+';
      }
      el.setAttribute('data-roon-filled', '1');
    }
  }

  function readStarOpts(el) {
    return {
      fill: el.getAttribute('data-roon-star-fill') || STAR_FILL,
      empty: el.getAttribute('data-roon-star-empty') || STAR_EMPTY
    };
  }

  // --- default card grid renderer ------------------------------------------
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function defaultCard(rev, opts) {
    opts = opts || {};
    var text = rev.body || rev.quote;
    var headline = rev.title || rev.quote;
    var tagHTML = '';
    if (rev.tags && rev.tags.length) {
      tagHTML = '<div class="roon-rc__tags">' +
        rev.tags.slice(0, 3).map(function (t) { return '<span>' + esc(t) + '</span>'; }).join('') +
        '</div>';
    }
    // On a scent page, derived reviews came from the Discovery Set — label them.
    var srcHTML = opts.derived ? '<div class="roon-rc__src">Reviewed as part of the Discovery Set</div>' : '';
    var meta = esc(rev.author);
    if (!opts.derived && rev.productName) meta += ' · ' + esc(rev.productName);
    // Opt-in full-detail: every custom question this reviewer answered, expandable.
    var detailHTML = '';
    if (opts.detail && rev.allAnswers && rev.allAnswers.length) {
      detailHTML =
        '<button type="button" class="roon-rc__more" aria-expanded="false">See full review &#9662;</button>' +
        '<div class="roon-rc__detail" hidden>' +
          rev.allAnswers.map(qaRowHTML).join('') +
        '</div>';
    }
    return '<article class="roon-rc">' +
      '<div class="roon-rc__stars">' + starsHTML(rev.rating) + '</div>' +
      (rev.verified ? '<div class="roon-rc__badge">✓ Verified Buyer</div>' : '') +
      (headline ? '<h3 class="roon-rc__title">' + esc(headline) + '</h3>' : '') +
      '<p class="roon-rc__body">' + esc(text) + '</p>' +
      '<div class="roon-rc__meta">' + meta + '</div>' +
      srcHTML + tagHTML + detailHTML +
      '</article>';
  }

  // One row of the expandable detail: range questions render as a 5-dot scale
  // with the min/max labels; multi/single answers render as chips.
  function qaRowHTML(qa) {
    var label = '<span class="roon-rc__q">' + esc(qa.label) + '</span>';
    if (qa.type === 'range') {
      var val = parseInt(qa.answer[0], 10) || 0;
      var dots = '';
      for (var i = 1; i <= 5; i++) dots += '<span class="roon-rc__dot' + (i <= val ? ' on' : '') + '"></span>';
      var ends = qa.values
        ? '<span class="roon-rc__ends">' + esc(qa.values.min || '') + '<em>' + esc(qa.values.max || '') + '</em></span>'
        : '';
      return '<div class="roon-rc__qa">' + label +
        '<span class="roon-rc__scale"><span class="roon-rc__dots">' + dots + '</span>' + ends + '</span></div>';
    }
    var chips = qa.answer.map(function (a) { return '<span>' + esc(a) + '</span>'; }).join('');
    return '<div class="roon-rc__qa">' + label + '<span class="roon-rc__achips">' + chips + '</span></div>';
  }

  function renderGrid(container, reviews, opts) {
    opts = opts || {};
    var limit = parseInt(container.getAttribute('data-roon-limit') || '0', 10);
    var withText = reviews.filter(function (r) { return (r.body || r.quote); });
    if (limit > 0) withText = withText.slice(0, limit);
    container.innerHTML = withText.map(function (r) { return defaultCard(r, opts); }).join('');
    container.setAttribute('data-roon-rendered', '1');
    // Reveal/hide a labeled wrapper (e.g. the scent backfill section) by content.
    var section = container.closest('[data-roon-scent-section]');
    if (section) {
      if (withText.length) section.removeAttribute('hidden');
      else section.setAttribute('hidden', '');
    }
  }

  function fillGrids(data) {
    var nodes = document.querySelectorAll('[data-roon-reviews]');
    if (!nodes.length) return;
    var all = normalize(data);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.getAttribute('data-roon-rendered')) continue;
      var scope = el.getAttribute('data-roon-reviews');
      var list = all, opts = {};
      if (el.getAttribute('data-roon-detail') === 'full') opts.detail = true;
      if (scope && scope.indexOf('scent:') === 0) {
        // Backfill section: Discovery Set reviews that picked this scent
        // (own-product reviews already live in the Klaviyo widget below).
        var scentKey = scope.slice(6);
        var ownId = el.getAttribute('data-roon-product') || '';
        list = all.filter(function (r) {
          return String(r.productId) !== String(ownId) && reviewMatchesScent(r, scentKey);
        });
        opts.derived = true;
      } else if (scope && scope !== 'store' && scope !== 'all') {
        var pid = scope.replace(/^product:/, '');
        list = all.filter(function (r) { return String(r.productId) === pid; });
      }
      renderGrid(el, list, opts);
    }
  }

  // --- public API + boot ----------------------------------------------------
  var readyCbs = [];
  var _data = null, _failed = false;

  function ready(cb) {
    if (_data) { cb(_data); return; }
    if (_failed) { return; }
    readyCbs.push(cb);
  }

  function injectCardCSS() {
    if (document.getElementById('roon-rc-css')) return;
    var css = '' +
      '.roon-rc{position:relative;border:1px solid #ebebeb;border-radius:12px;padding:18px;background:#fff;text-align:left}' +
      '.roon-rc__stars{font-size:16px;line-height:1;margin-bottom:12px}' +
      '.roon-rc__badge{position:absolute;right:14px;top:14px;background:#f4f4f4;border:1px solid #e9e9e9;border-radius:999px;padding:4px 10px;font-size:11px;color:#5c5c5c}' +
      '.roon-rc__title{font-size:17px;line-height:1.2;margin:0 0 8px;font-weight:600}' +
      '.roon-rc__body{font-size:14px;line-height:1.5;color:#3f3f3f;margin:0 0 12px}' +
      '.roon-rc__meta{font-weight:600;font-size:13px;margin-top:2px}' +
      '.roon-rc__tags{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px}' +
      '.roon-rc__tags span{font-size:11px;background:#f5f5f5;border-radius:999px;padding:4px 9px;color:#666;border:1px solid #efefef}' +
      '.roon-rc__more{margin-top:12px;font-size:12px;font-weight:600;color:#8b6a4a;background:none;border:none;padding:0;cursor:pointer;letter-spacing:.02em}' +
      '.roon-rc__detail{margin-top:12px;padding-top:12px;border-top:1px solid #efe9df;display:flex;flex-direction:column;gap:10px}' +
      '.roon-rc__qa{display:flex;flex-direction:column;gap:4px}' +
      '.roon-rc__q{font-size:11px;font-weight:600;color:#6b5d49;letter-spacing:.01em}' +
      '.roon-rc__achips{display:flex;gap:5px;flex-wrap:wrap}' +
      '.roon-rc__achips span{font-size:11px;background:#f6f2ea;border:1px solid #ece4d6;border-radius:999px;padding:3px 8px;color:#4a4034}' +
      '.roon-rc__scale{display:flex;align-items:center;gap:10px}' +
      '.roon-rc__dots{display:inline-flex;gap:4px}' +
      '.roon-rc__dot{width:9px;height:9px;border-radius:50%;background:#e7ded0}' +
      '.roon-rc__dot.on{background:#CAA46B}' +
      '.roon-rc__ends{display:flex;gap:6px;font-size:10px;color:#9a8d78}' +
      '.roon-rc__ends em{font-style:normal}';
    var st = document.createElement('style');
    st.id = 'roon-rc-css';
    st.textContent = css;
    document.head.appendChild(st);
  }

  function boot() {
    injectCardCSS();
    // Expand/collapse the full-detail panel (delegated; works for cards injected later).
    if (!document._roonDetailBound) {
      document._roonDetailBound = true;
      document.addEventListener('click', function (e) {
        var b = e.target && e.target.closest && e.target.closest('.roon-rc__more');
        if (!b) return;
        var panel = b.nextElementSibling;
        if (!panel || !panel.classList.contains('roon-rc__detail')) return;
        if (panel.hasAttribute('hidden')) {
          panel.removeAttribute('hidden');
          b.innerHTML = 'Hide details &#9652;';
          b.setAttribute('aria-expanded', 'true');
        } else {
          panel.setAttribute('hidden', '');
          b.innerHTML = 'See full review &#9662;';
          b.setAttribute('aria-expanded', 'false');
        }
      });
    }
    fetchAll().then(function (data) {
      _data = data;
      try { fillRatings(data); } catch (e) {}
      try { fillGrids(data); } catch (e) {}
      readyCbs.forEach(function (cb) { try { cb(data); } catch (e) {} });
      readyCbs = [];
      document.dispatchEvent(new CustomEvent('roon:reviews', { detail: data }));
      // Collection grids, filters, sort, pagination and quick-views inject
      // [data-roon-rating] cards client-side after this first pass. Watch for
      // them and fill on the fly (fillRatings skips already-filled nodes).
      var t = null;
      try {
        new MutationObserver(function () {
          if (t) return;
          t = setTimeout(function () {
            t = null;
            try { fillRatings(_data); } catch (e) {} // idempotent; skips filled nodes
          }, 120);
        }).observe(document.body, { childList: true, subtree: true });
      } catch (e) {}
    }).catch(function (e) {
      _failed = true;
      // graceful: leave existing DOM (fallback copy) untouched
      if (window.console) console.warn('[RoonReviews] load failed', e);
    });
  }

  window.RoonReviews = {
    fetchAll: fetchAll,
    ready: ready,
    aggregate: aggregate,
    normalize: normalize,
    starsHTML: starsHTML,
    refresh: function () { _data = null; _failed = false; _promise = null; try { sessionStorage.removeItem(CACHE_KEY); } catch (e) {} boot(); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/*
 * Roon Body — Klaviyo reviews summary bar charts (roon-qbars)
 * Klaviyo renders the text / multi-select custom-question summaries
 * ("What stands out about Roon?", "How do you use Roon?", …) as a plain
 * comma-list of "Answer (count)" — visually cluttered, no hierarchy.
 * This rewrites each into a sorted horizontal bar chart (brand-tan fill
 * on a light track), matching the look of Klaviyo's own range sliders.
 * Styles live in roon-custom.css (.roon-qbars / .roon-qbar__*).
 *
 * The summary is rendered async by Klaviyo and re-rendered when the
 * "View all" panel expands, so we decorate on load AND via a debounced
 * MutationObserver. Decoration is idempotent (data-roon-qbars marker)
 * and preserves Klaviyo's click-to-filter behavior by proxying each
 * row's click to the original (now hidden) answer span.
 */
(function () {
  'use strict';
  var Q_SEL = '.kl_reviews__summary__custom_question--text';
  var A_SEL = '.kl_reviews__summary__custom_question__answer--text';
  var R_SEL = '.kl_reviews__summary__custom_question_response';

  function decorate(block) {
    if (block.getAttribute('data-roon-qbars')) return;
    var answer = block.querySelector(A_SEL);
    if (!answer) return;
    var spans = [].slice.call(answer.querySelectorAll(R_SEL));
    if (!spans.length) return;

    var items = spans.map(function (s) {
      var m = s.textContent.match(/\((\d+)\)/);
      var count = m ? parseInt(m[1], 10) : 0;
      // strip trailing " · " separator first, then the "(count)" suffix
      var label = s.textContent.replace(/\s*·\s*$/, '').replace(/\s*\(\d+\)\s*$/, '').trim();
      return { label: label, count: count, el: s };
    }).filter(function (it) { return it.label; });
    if (!items.length) return;

    items.sort(function (a, b) { return b.count - a.count; });
    var max = items.reduce(function (m, it) { return Math.max(m, it.count); }, 0) || 1;

    var wrap = document.createElement('div');
    wrap.className = 'roon-qbars';
    items.forEach(function (it) {
      var pct = Math.max(3, Math.round(it.count / max * 100));
      var row = document.createElement('button');
      row.type = 'button';
      row.className = 'roon-qbar__row';
      row.innerHTML =
        '<span class="roon-qbar__top"><span class="roon-qbar__label"></span>' +
        '<span class="roon-qbar__count"></span></span>' +
        '<span class="roon-qbar__track"><span class="roon-qbar__fill"></span></span>';
      row.querySelector('.roon-qbar__label').textContent = it.label;
      row.querySelector('.roon-qbar__count').textContent = it.count;
      row.querySelector('.roon-qbar__fill').style.width = pct + '%';
      row.addEventListener('click', function () { try { it.el.click(); } catch (e) {} });
      wrap.appendChild(row);
    });

    answer.style.display = 'none';
    answer.parentNode.insertBefore(wrap, answer.nextSibling);
    block.setAttribute('data-roon-qbars', '1');
  }

  function scan() {
    var blocks = document.querySelectorAll(Q_SEL);
    for (var i = 0; i < blocks.length; i++) {
      try { decorate(blocks[i]); } catch (e) {}
    }
  }

  function boot() {
    scan();
    var t = null;
    var obs = new MutationObserver(function () {
      if (t) return;
      t = setTimeout(function () { t = null; scan(); }, 120);
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
